import { FunctionComponent, useContext, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import ModalHeader from "../shared/modalHeader"
import ModalFooter from "../shared/modalFooter"
import { useViewerContext } from "../../context/ViewerContext"
import { LabelValueOptionType } from "../../types/commonTypes"
import Select, { GroupBase, StylesConfig } from "react-select"
import styles from "./styles.module.scss"
import Input from "../shared/input"
import {
    disableSubmitInviteUser,
    getInviteUserFormInitialOrgState,
    sanitizeInviteUserInputData,
    validateInviteUserInputData,
} from "./util"
import { InviteUserModalI, NewUserErrorFormI } from "./types"
import { USER_INVITE } from "../../graphql/mutations/add_user"
import { useMutation } from "@apollo/client"
import Alert from "../shared/alert"
import { GET_USERS } from "../../graphql/queries/users"
import {
    AddUsersMutationVariables,
    SubjectRole,
    ZoneInfo,
} from "../../generated/graphql"
import { TrexNavigator } from "../../classes/navigator/navigator"
import { sortArrayByKey } from "../../util/arrays/array"
import { Toggle } from "../shared/selectionControls"
import Policy_OrganizationMembership from "../../rolePolicy/organizationMembership"
import ToolTip_Popover from "../shared/toolTip/toolTip_popover"
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ConfigContext } from "../../context/ConfigContexts"
import { objectHasDefinedFields } from "../../util/objects/objects"

const InviteUsersModal = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { getViewer } = useViewerContext()
    const { role: viewerRole, organizationAndDescendants } = getViewer()

    /** Modal state */
    const [inviteUserModalState, setInviteUserModalState] =
        useState<InviteUserModalI>({
            newUserState: {
                firstName: "",
                lastName: "",
                email: "",
                inviteeUserRole: SubjectRole.OrgMember,
                organization: getInviteUserFormInitialOrgState(
                    organizationAndDescendants
                ),
                timezone: ZoneInfo.CentralTimeZone,
                fullAccess: true,
            },
            errorState: {
                email: undefined,
                firstName: undefined,
                lastName: undefined,
                organization: undefined,
            },
            disableSubmit: true,
        })
    const [inviteUserErrorBanner, setInviteUserErrorBanner] = useState(false)

    const [inviteUserMutation, { loading: mutationLoading }] =
        useMutation(USER_INVITE)

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const value = e.target.value
        const name = e.target.name as keyof NewUserErrorFormI

        const updatedNewUserState = {
            ...inviteUserModalState.newUserState,
            [name]: value,
        }
        const disableSubmit = disableSubmitInviteUser(updatedNewUserState)

        // if user has corrected the error, we can clear the error state for the field they are typing into.
        // else we should wait until blur.  This creates the best user experience
        const errorState = { ...inviteUserModalState.errorState }
        if (errorState[name] !== undefined) {
            const updatedErrors = validateInviteUserInputData(
                inviteUserModalState.newUserState
            )
            errorState[name] = updatedErrors[name]
        }

        setInviteUserModalState((prevState) => ({
            ...prevState,
            newUserState: updatedNewUserState,
            errorState: errorState,
            disableSubmit: disableSubmit,
        }))
    }

    const handleSetInviteUserOrganization = (
        value: LabelValueOptionType | null
    ) => {
        if (!value) {
            throw new Error(
                "Error: handleSetInviteUserOrganization. Should not be setting organization to null."
            )
        }

        const updatedNewUserState = {
            ...inviteUserModalState.newUserState,
            organization: value,
        }

        const disableSubmit = disableSubmitInviteUser(updatedNewUserState)

        setInviteUserModalState((prevState) => ({
            ...prevState,
            newUserState: updatedNewUserState,
            disableSubmit: disableSubmit,
        }))
    }

    const handleFullAccessChange = () => {
        const updatedFullAccess = !inviteUserModalState.newUserState.fullAccess

        // if the user is setting full access, they can invite any role
        // if the user is setting limited access, they can only invite org members - other roles will be disabled in the dropdown
        const updatedRole = updatedFullAccess
            ? inviteUserModalState.newUserState.inviteeUserRole
            : SubjectRole.OrgMember

        setInviteUserModalState((prevState) => ({
            ...prevState,
            newUserState: {
                ...inviteUserModalState.newUserState,
                inviteeUserRole: updatedRole,
                fullAccess: updatedFullAccess,
            },
        }))
    }

    // blur handlers
    const handleEmailBlur = () => {
        const updatedErrorData = validateInviteUserInputData(
            inviteUserModalState.newUserState
        )

        const updatedErrorState: NewUserErrorFormI = {
            ...inviteUserModalState.errorState,
            email: updatedErrorData.email,
        }

        setInviteUserModalState((prevState) => ({
            ...prevState,
            errorState: updatedErrorState,
        }))
    }

    const handleFirstNameBlur = () => {
        const updatedErrorData = validateInviteUserInputData(
            inviteUserModalState.newUserState
        )

        const updatedErrorState: NewUserErrorFormI = {
            ...inviteUserModalState.errorState,
            firstName: updatedErrorData.firstName,
        }

        setInviteUserModalState((prevState) => ({
            ...prevState,
            errorState: updatedErrorState,
        }))
    }

    const handleLastNameBlur = () => {
        const updatedErrorData = validateInviteUserInputData(
            inviteUserModalState.newUserState
        )

        const updatedErrorState: NewUserErrorFormI = {
            ...inviteUserModalState.errorState,
            lastName: updatedErrorData.lastName,
        }

        setInviteUserModalState((prevState) => ({
            ...prevState,
            errorState: updatedErrorState,
        }))
    }

    // handleSubmitInviteUser
    // Check policy
    // Validate form and sanitize form
    // Call mutation
    const handleSubmitInviteUser = async () => {
        // Check policy
        const canActiveUserInvite =
            Policy_OrganizationMembership.canInviteUser(viewerRole)
        if (!canActiveUserInvite) {
            // This will not happen because only users with the correct permissions can access this modal. This is a safety check.
            throw new Error(
                `${inviteUserModalState.newUserState.inviteeUserRole} does not have permission to invite new users.`
            )
        }

        // Validate form
        const errorState = validateInviteUserInputData(
            inviteUserModalState.newUserState
        )
        const hasErrors = objectHasDefinedFields(errorState)
        if (hasErrors) {
            // This will not happen because the form is disabled if the form is not valid. This is a safety check.
            throw new Error("Form is not valid and should not be submitted")
        }

        // sanitize
        const sanitizedForm = sanitizeInviteUserInputData(
            inviteUserModalState.newUserState
        )

        const inviteUserVariables: AddUsersMutationVariables = {
            UsersAddInput: {
                users: [
                    {
                        identity: {
                            email: sanitizedForm.email,
                            firstName: sanitizedForm.firstName,
                            lastName: sanitizedForm.lastName,
                            zoneInfo: sanitizedForm.timezone,
                        },
                        organizationID: sanitizedForm.organization?.value || "",
                        role: sanitizedForm.inviteeUserRole,
                        managedAccess: !sanitizedForm.fullAccess,
                    },
                ],
            },
        }

        await inviteUserMutation({
            variables: inviteUserVariables,
            refetchQueries: [GET_USERS],
        })
            .then(() => {
                navigate(-1)
            })
            .catch((error) => {
                console.error("Invite user mutation errror ", error)
                setInviteUserErrorBanner(true)
            })
    }

    return (
        <div className={styles.container}>
            <ModalHeader
                trexNavigator={
                    new TrexNavigator(
                        {
                            navigateTo: -1,
                            locationKey: location.key,
                            pathName: location.pathname,
                        },
                        navigate
                    )
                }
                title="Invite Users"
            />
            <InviteUsersBody
                modalState={inviteUserModalState}
                handleInputChange={handleInputChange}
                handleOrganizationChange={handleSetInviteUserOrganization}
                organizations={organizationAndDescendants}
                handleEmailBlur={handleEmailBlur}
                handleFirstNameBlur={handleFirstNameBlur}
                handleLastNameBlur={handleLastNameBlur}
                viewerRole={viewerRole}
                handleFullAccessChange={handleFullAccessChange}
            />
            {inviteUserErrorBanner && (
                <Alert
                    isCloseable={false}
                    message={
                        <div>
                            There was a problem adding the user. Please close
                            the modal and try again.
                        </div>
                    }
                    alertType="danger"
                />
            )}
            <ModalFooter
                advanceText="Submit"
                handleAdvanceClick={handleSubmitInviteUser}
                disableAdvance={
                    inviteUserModalState.disableSubmit ||
                    mutationLoading ||
                    inviteUserErrorBanner
                }
            />
        </div>
    )
}

export default InviteUsersModal

interface InviteUserBodyPropsInterface {
    organizations: LabelValueOptionType[]
    modalState: InviteUserModalI
    handleInputChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => void
    handleOrganizationChange: (value: LabelValueOptionType | null) => void
    handleFullAccessChange: () => void
    handleEmailBlur: () => void
    handleFirstNameBlur: () => void
    handleLastNameBlur: () => void
    viewerRole: SubjectRole
}

const InviteUsersBody: FunctionComponent<InviteUserBodyPropsInterface> = ({
    modalState,
    organizations,
    handleOrganizationChange,
    handleInputChange,
    handleEmailBlur,
    handleFirstNameBlur,
    handleLastNameBlur,
    handleFullAccessChange,
    viewerRole,
}) => {
    const { newUserState: form, errorState: errorForm } = modalState

    const { getViewer } = useViewerContext()
    const config = useContext(ConfigContext)
    const isAlphaTester = config.alphaTestersUserIds.includes(
        getViewer().userId
    )

    const sortedOrganizations = sortArrayByKey(
        organizations,
        (obj) => obj.label,
        false
    )

    const roleOptions: LabelValueOptionType[] =
        Policy_OrganizationMembership.getPermittedInviteRoles(viewerRole).map(
            (option) => ({
                ...option,
                disabled:
                    !form.fullAccess &&
                    (option.value === SubjectRole.OrgAdmin ||
                        option.value === SubjectRole.PpoAdmin),
            })
        )

    return (
        <div className={styles.bodyContainer}>
            <div className={styles.organizationContainer}>
                <div className={styles.organizationTitle}>Organization</div>
                <Select
                    options={sortedOrganizations}
                    isSearchable={true}
                    onChange={handleOrganizationChange}
                    value={form.organization}
                    styles={reactSelectStyles}
                    placeholder={"Select Organization"}
                    isDisabled={false}
                />
            </div>
            <div className={styles.instructions}>
                The new user will receive emailed instructions on how to login.
            </div>
            <div className={styles.rowContainer}>
                <div className={styles.row_leftContainer}>
                    <Input
                        orientation="vertical"
                        type="text"
                        value={form.firstName}
                        onChange={handleInputChange}
                        label="First Name"
                        disabled={false}
                        name="firstName"
                        message={errorForm.firstName}
                        specializedClass={
                            errorForm.firstName ? "danger" : "default"
                        }
                        onBlur={handleFirstNameBlur}
                    />
                </div>
                <div className={styles.row_rightContainer}>
                    <Input
                        orientation="vertical"
                        type="text"
                        value={form.lastName}
                        onChange={handleInputChange}
                        label="Last Name"
                        disabled={false}
                        name="lastName"
                        message={errorForm.lastName}
                        specializedClass={
                            errorForm.lastName ? "danger" : "default"
                        }
                        onBlur={handleLastNameBlur}
                    />
                </div>
            </div>
            <Input
                orientation="vertical"
                type="text"
                value={form.email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                label="Email"
                disabled={false}
                name="email"
                message={errorForm.email}
                specializedClass={errorForm.email ? "danger" : "default"}
            />
            <div className={styles.rowContainer}>
                {isAlphaTester && (
                    <div className={styles.row_leftContainer}>
                        <div className={styles.toggleLabel}>
                            <span className={styles.toggleLabelText}>
                                Access Level
                            </span>
                            <ToolTip_Popover
                                content={
                                    <div className={styles.toggleDescription}>
                                        Full access allows access to all wells.
                                        Limited access allows access to only the
                                        wells assigned to the user. Access can
                                        be updated from the User Admin page.
                                        <br />
                                        Note: Only organization members can have
                                        limited access.
                                    </div>
                                }
                                customTooltipContentStyles={styles.popover}
                                triggerChildren={
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        className={styles.infoIcon}
                                    />
                                }
                                popover={{
                                    removePopoverButtonStyling: true,
                                }}
                            />
                        </div>
                        <div className={styles.toggleContainer}>
                            <Toggle
                                checked={form.fullAccess}
                                handleToggleClick={handleFullAccessChange}
                                toggleLabel=""
                            />
                            <div className={styles.toggleValue}>
                                <b>Access:</b>{" "}
                                {form.fullAccess ? "Full" : "Limited"}
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.row_rightContainer}>
                    <Input
                        onChange={handleInputChange}
                        orientation="vertical"
                        type="select"
                        options={roleOptions}
                        disabled={false}
                        value={form.inviteeUserRole}
                        label="Role"
                        name="inviteeUserRole"
                    />
                </div>
            </div>
            <div className={styles.rowContainer}>
                <div className={styles.row_leftContainer}>
                    <Input
                        onChange={handleInputChange}
                        orientation="vertical"
                        type="select"
                        options={[
                            {
                                label: "Central Time Zone",
                                value: ZoneInfo.CentralTimeZone,
                            },
                            {
                                label: "Eastern Time Zone",
                                value: ZoneInfo.EasternTimeZone,
                            },
                            {
                                label: "Mountain Time Zone",
                                value: ZoneInfo.MountainTimeZone,
                            },
                            {
                                label: "Pacific Time Zone",
                                value: ZoneInfo.PacificTimeZone,
                            },
                        ]}
                        disabled={false}
                        value={form.timezone}
                        label="Time Zone"
                        name="timezone"
                    />
                </div>
            </div>
        </div>
    )
}

const reactSelectStyles: StylesConfig<
    LabelValueOptionType,
    false,
    GroupBase<LabelValueOptionType>
> = {
    control: (provided, state) => ({
        ...provided,
        boxSizing: "border-box",
        outline: "none",
        padding: ".0 .25rem",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: state.isFocused ? "#64b5f6" : "#cfd8dc",
        borderRadius: "4px",
        backgroundColor: "white",
        fontWeight: "400",
        fontSize: "16px",
        lineHeight: "24px",
        color: "#37474f",
        height: "32px",
        boxShadow: state.isFocused
            ? "0px 0px 0px 3.2px rgba(0, 123, 255, 0.25)"
            : "none",
        width: "339px",
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? "#64b5f6"
            : state.isFocused
            ? "#f5f5f5"
            : "transparent",

        color: state.isSelected ? "white" : "inherit",
    }),
}
